// One series, neutral ink, 2px line, last point marked. Hover reports the
// nearest point to the parent so the tile can show it as a readout.
import React from 'react';

export interface SparkPoint { t: number; v: number }

export function Sparkline({ points, width = 132, height = 30, onHover, title }: {
  points: Array<[number, number]>;
  width?: number; height?: number;
  onHover?: (p: SparkPoint | null) => void;
  title?: string;
}) {
  const pad = 3;
  const n = points.length;
  if (n === 0) {
    return <svg width={width} height={height} role="img" aria-label="no data"><line x1={pad} x2={width - pad} y1={height / 2} y2={height / 2} stroke="var(--fg-5)" strokeWidth={1} strokeDasharray="2 3" /></svg>;
  }
  const t0 = points[0][0]; const t1 = points[n - 1][0];
  const vs = points.map((p) => p[1]);
  const vmin = Math.min(0, ...vs); const vmax = Math.max(...vs, vmin + 1e-9);
  const x = (t: number) => (n === 1 ? width / 2 : pad + ((t - t0) / (t1 - t0 || 1)) * (width - pad * 2));
  const y = (v: number) => height - pad - ((v - vmin) / (vmax - vmin)) * (height - pad * 2);
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(' ');
  const last = points[n - 1];

  const move = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!onHover) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    let best = 0; let bestD = Infinity;
    for (let i = 0; i < n; i++) { const dd = Math.abs(x(points[i][0]) - px); if (dd < bestD) { bestD = dd; best = i; } }
    onHover({ t: points[best][0], v: points[best][1] });
  };

  return (
    <svg width={width} height={height} onMouseMove={move} onMouseLeave={() => onHover?.(null)} style={{ display: 'block', cursor: onHover ? 'crosshair' : 'default' }} role="img" aria-label={title}>
      {title && <title>{title}</title>}
      <path d={d} fill="none" stroke="var(--fg-2)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(last[0])} cy={y(last[1])} r={3} fill="var(--fg)" stroke="var(--bg-panel)" strokeWidth={2} />
    </svg>
  );
}
